/**
 * @since 2026-04-18
 * @author Francesco Parrella
 * @maintainer Francesco Parrella
 * @copyright AlgoTraders, All rights reserved
 */

import { getAuth } from "firebase/auth";
import { getConfig } from "../getConfig";

const { BACKEND_URL = "http://localhost:8000" } = getConfig();

export class AuthenticationError extends Error {
  constructor(message) {
    super(message);
    this.name = "AuthenticationError";
  }
}

async function __request(endpoint, data, idToken, getToken = null) {
  const doFetch = (token) =>
    fetch(`${BACKEND_URL}/api/${endpoint}/`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify(data || {}),
    });

  let response = await doFetch(idToken);

  if (response.status === 401) {
    let freshToken = null;
    if (getToken) {
      try { freshToken = await getToken(true); } catch {}
    }
    if (!freshToken) {
      try {
        const auth = getAuth();
        if (auth?.currentUser) freshToken = await auth.currentUser.getIdToken(true);
      } catch {}
    }
    if (freshToken && freshToken !== idToken) {
      response = await doFetch(freshToken);
    }
  }

  if (!response.ok) {
    let errorMessage = `HTTP ${response.status}`;
    try {
      const parsed = await response.json();
      errorMessage = parsed.error || errorMessage;
    } catch {}
    if (response.status === 401) throw new AuthenticationError(errorMessage);
    const err = new Error(errorMessage);
    err.status = response.status;
    throw err;
  }

  return response.json();
}

export const getChecksCatalog = (idToken = null, getToken = null) =>
  __request("checksCatalog", {}, idToken, getToken);

export const getUniverse = (idToken = null, getToken = null) =>
  __request("universe", {}, idToken, getToken);

export const getSecurity = (securityName, barSize, idToken = null, getToken = null) =>
  __request("security", { securityName, barSize }, idToken, getToken);

export const validateIssue = (payload, idToken = null, getToken = null) =>
  __request("issues/validate", payload, idToken, getToken);

export const unvalidateIssue = (issueId, idToken = null, getToken = null) =>
  __request("issues/unvalidate", { issueId }, idToken, getToken);

export const listOverrides = (filters = {}, idToken = null, getToken = null) =>
  __request("overrides/list", filters, idToken, getToken);

export const createOverride = (payload, idToken = null, getToken = null) =>
  __request("overrides/create", payload, idToken, getToken);

export const deactivateOverride = (overrideId, idToken = null, getToken = null) =>
  __request("overrides/deactivate", { overrideId }, idToken, getToken);

export const deactivateOverridesMany = (overrideIds, idToken = null, getToken = null) =>
  __request("overrides/deactivateMany", { overrideIds }, idToken, getToken);

export const startFixJob = (payload, idToken = null, getToken = null) =>
  __request("fixJob/start", payload, idToken, getToken);

export const getFixJobStatus = (jobId, idToken = null, getToken = null) =>
  __request("fixJob/status", { jobId }, idToken, getToken);

export const listFixJobs = (limit = 20, idToken = null, getToken = null) =>
  __request("fixJob/list", { limit }, idToken, getToken);
